<script setup>
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  buildCsv,
  csvHeadersFor,
  downloadCsv,
} from 'dashboard/helper/arrowsAiCsv';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  isImporting: { type: Boolean, default: false },
});

const emit = defineEmits(['import']);
const { t, locale } = useI18n();

const dialogRef = ref(null);
const fileInput = ref(null);
const hasSelectedFile = ref(null);
const selectedFileName = ref('');

const processFileName = fileName => {
  const lastDotIndex = fileName.lastIndexOf('.');
  const extension = fileName.slice(lastDotIndex);
  const baseName = fileName.slice(0, lastDotIndex);

  return baseName.length > 20
    ? `${baseName.slice(0, 20)}...${extension}`
    : fileName;
};

const handleFileClick = () => fileInput.value?.click();

const handleFileChange = () => {
  const file = fileInput.value?.files[0];
  hasSelectedFile.value = file;
  selectedFileName.value = file ? processFileName(file.name) : '';
};

const handleRemoveFile = () => {
  hasSelectedFile.value = null;
  selectedFileName.value = '';
  if (fileInput.value) fileInput.value.value = null;
};

const uploadFile = () => {
  if (!hasSelectedFile.value) return;
  emit('import', hasSelectedFile.value);
};

const downloadSample = () => {
  const headers = csvHeadersFor(
    locale.value,
    ['short_code', 'content'],
    ['codigo', 'mensaje']
  );
  const rows = [
    [
      t('CANNED_MGMT.IMPORT.SAMPLE.SHORT_CODE_1'),
      t('CANNED_MGMT.IMPORT.SAMPLE.CONTENT_1'),
    ],
    [
      t('CANNED_MGMT.IMPORT.SAMPLE.SHORT_CODE_2'),
      t('CANNED_MGMT.IMPORT.SAMPLE.CONTENT_2'),
    ],
  ];
  const filename = csvHeadersFor(
    locale.value,
    'import-canned-responses-sample.csv',
    'ejemplo-respuestas-predefinidas.csv'
  );
  downloadCsv(filename, buildCsv(headers, rows));
};

defineExpose({ dialogRef });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="t('CANNED_MGMT.IMPORT.TITLE')"
    :confirm-button-label="t('CANNED_MGMT.IMPORT.CONFIRM')"
    :is-loading="props.isImporting"
    :disable-confirm-button="props.isImporting || !hasSelectedFile"
    @confirm="uploadFile"
    @close="handleRemoveFile"
  >
    <template #description>
      <p class="mb-0 text-sm text-n-slate-11">
        {{ t('CANNED_MGMT.IMPORT.DESCRIPTION') }}
        <button type="button" class="text-n-blue-11" @click="downloadSample">
          {{ t('CANNED_MGMT.IMPORT.DOWNLOAD_LABEL') }}
        </button>
      </p>
    </template>

    <div class="flex flex-col gap-2">
      <div class="flex items-center gap-2">
        <label class="text-sm text-n-slate-12 whitespace-nowrap">
          {{ t('CANNED_MGMT.IMPORT.LABEL') }}
        </label>
        <div class="flex items-center justify-between w-full gap-2">
          <span v-if="hasSelectedFile" class="text-sm text-n-slate-12">
            {{ selectedFileName }}
          </span>
          <Button
            v-if="!hasSelectedFile"
            :label="t('CANNED_MGMT.IMPORT.CHOOSE_FILE')"
            icon="i-lucide-upload"
            color="slate"
            variant="ghost"
            size="sm"
            type="button"
            class="!w-fit"
            @click="handleFileClick"
          />
          <div v-else class="flex items-center gap-1">
            <Button
              :label="t('CANNED_MGMT.IMPORT.CHANGE')"
              color="slate"
              variant="ghost"
              size="sm"
              type="button"
              @click="handleFileClick"
            />
            <div class="w-px h-3 bg-n-strong" />
            <Button
              icon="i-lucide-trash"
              color="slate"
              variant="ghost"
              size="sm"
              type="button"
              @click="handleRemoveFile"
            />
          </div>
        </div>
      </div>
    </div>
    <input
      ref="fileInput"
      type="file"
      accept=".csv,text/csv"
      class="hidden"
      @change="handleFileChange"
    />
  </Dialog>
</template>
